<template>
    <v-container fluid class="login-container" :class="instanceName" style="height: 100vh">
        <v-row class="signin fill-height">
            <v-col cols="12" md="8" align-self="center">
                <p class="text-h1 white--text text-center shadow">
                    <span class="typed-text">{{ typeValue }}</span>
                    <span class="blinking-cursor">|</span>
                    <span class="cursor" :class="{ typing: typeStatus }">&nbsp;</span>
                </p>
            </v-col>
            <v-col cols="12" md="4" class="pa-8" align-self="center" align="center">
                <v-card class="right pa-8" elevation="16" style="max-width: 400px">
                    <v-img contain :src="require('../../assets/intrabooking-white.png')" transition="scale-transition" />

                    <template v-if="screen === 'login'">
                        <p class="text-h4 text-center mt-8 mb-8">Iniciar Sesión</p>
                        <p v-if="lookEmailText" class="text-center text-body-2 mb-4">{{ lookEmailText }}</p>
                        <v-text-field v-model="username" label="Nombre de usuario" required outlined ref="username" />
                        <v-text-field
                            v-model="password"
                            label="Contraseña"
                            :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPass = !showPass"
                            required
                            outlined
                            :type="showPass ? 'text' : 'password'"
                            @keydown.enter="login"
                        />
                        <v-btn color="primary" block large @click="login" :disabled="!username || !password" :loading="loggingIn">Iniciar sesión</v-btn>
                        <v-btn text block small class="mt-2" @click="screen = 'recover'">¿Olvidaste tu contraseña?</v-btn>
                    </template>

                    <template v-else-if="screen === 'recover'">
                        <p class="text-h5 text-center mt-8 mb-4">Recuperar contraseña</p>
                        <p class="text-center mb-4 text-body-2">Introduce tu nombre de usuario o teléfono móvil y te enviaremos un correo con las instrucciones para acceder.</p>
                        <v-text-field v-model="username" label="Nombre de usuario" required outlined />
                        <v-btn color="primary" small block large @click="recover" :disabled="!username" :loading="recovering">Recuperar</v-btn>
                    </template>

                    <pre class="red--text mt-8 text-center">{{ errors }}</pre>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style lang="scss" scoped>
.login-container {
    background: url(/public/images/login-backgrounds/default.jpg);
    background-repeat: no-repeat;
    background-size: cover;

    // background if this is included in v-app witrh a class of palmaaquarium
    .palmaaquarium & {
        background: url(/public/images/login-backgrounds/palmaaquarium.jpg);
        background-repeat: no-repeat;
        background-size: cover;
    }

    .aspro & {
        background: url(/public/images/login-backgrounds/aspro.jpg);
        background-repeat: no-repeat;
        background-size: cover;
    }

    .shadow {
        text-shadow: 4px 4px 8px #000000;
    }
}

// Cursor blinking CSS Starts...
.blinking-cursor {
    font-size: 6rem;
    color: #fff;
    -webkit-animation: 1s blink step-end infinite;
    -moz-animation: 1s blink step-end infinite;
    -ms-animation: 1s blink step-end infinite;
    -o-animation: 1s blink step-end infinite;
    animation: 1s blink step-end infinite;
}
@keyframes blink {
    from,
    to {
        color: transparent;
    }
    50% {
        color: #fff;
    }
}
@-moz-keyframes blink {
    from,
    to {
        color: transparent;
    }
    50% {
        color: #fff;
    }
}
@-webkit-keyframes blink {
    from,
    to {
        color: transparent;
    }
    50% {
        color: #fff;
    }
}
@-ms-keyframes blink {
    from,
    to {
        color: transparent;
    }
    50% {
        color: #fff;
    }
}
@-o-keyframes blink {
    from,
    to {
        color: transparent;
    }
    50% {
        color: #fff;
    }
}
// Cursor blinking CSS Ends...
</style>

<script lang="ts">
import { loginStore } from "@/stores/LoginStore";
import { Component, Vue } from "vue-property-decorator";

import { LogUserInResponse } from "@/services/ClientInterfaces";
import { DataClient } from "@/services/DataClient";
import { ApiError } from "@/services/BaseService";
import { BaseView } from "@/components/BaseView";
import { Utilities } from "@/services/Utilities";

@Component
export default class extends BaseView {
    username = "";
    password = "";
    showPass = false;
    errors = "";

    typeValue = "";
    typeStatus = false;
    displayTextArray = ["Bienvenido", "Welcome", "Willkommen", "Benvenuto", "Ben-vindo", "Bienvenue"];
    typingSpeed = 100;
    erasingSpeed = 100;
    newTextDelay = 2000;
    displayTextArrayIndex = 0;
    charIndex = 0;

    screen = "login";
    lookEmailText = "";
    recovering = false;
    loggingIn = false;

    response: LogUserInResponse | null = null;

    created() {
        setTimeout(this.typeText, this.newTextDelay + 200);
        Utilities.focus(this, "username");
    }

    typeText() {
        if (this.charIndex < this.displayTextArray[this.displayTextArrayIndex].length) {
            if (!this.typeStatus) this.typeStatus = true;
            this.typeValue += this.displayTextArray[this.displayTextArrayIndex].charAt(this.charIndex);
            this.charIndex += 1;
            setTimeout(this.typeText, this.typingSpeed + Math.random() * this.typingSpeed);
        } else {
            this.typeStatus = false;
            setTimeout(this.eraseText, this.newTextDelay);
        }
    }

    eraseText() {
        if (this.charIndex > 0) {
            if (!this.typeStatus) this.typeStatus = true;
            this.typeValue = this.displayTextArray[this.displayTextArrayIndex].substring(0, this.charIndex - 1);
            this.charIndex -= 1;
            setTimeout(this.eraseText, this.erasingSpeed);
        } else {
            this.typeStatus = false;
            this.displayTextArrayIndex += 1;
            if (this.displayTextArrayIndex >= this.displayTextArray.length) this.displayTextArrayIndex = 0;
            setTimeout(this.typeText, this.typingSpeed + 1000);
        }
    }

    async login() {
        if (!this.username || !this.password) {
            return;
        }

        try {
            this.loggingIn = true;

            const [r] = await Promise.all([
                await DataClient.Users.logIn({
                    username: this.username,
                    password: this.password,
                }),
                this.sleep(1500),
            ]);

            loginStore().logIn(r);

            this.$router.push({ name: "Home" });
        } catch (e) {
            if (e instanceof ApiError) {
                this.errors = e.errorMessage;
            }
        }

        this.loggingIn = false;
        this.lookEmailText = "";
    }

    async recover() {
        this.recovering = true;
        try {
            this.lookEmailText = "Revisa tu correo. Te hemos enviado un enlace para recuperar tu contraseña. Si no lo encuentras, revisa la carpeta de spam o correo electrónico no deseado.";

            await Promise.all([
                this.sleep(1500),
                DataClient.Users.resetPassword({
                    username: this.username,
                }),
            ]);

            this.screen = "login";
        } catch (e) {
            if (e instanceof ApiError) {
                this.errors = e.errorMessage;
            }
        } finally {
            this.recovering = false;
        }
    }
}
</script>
